import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

const PrivacyPolicy = () => {
    const pageTransition = usePageTransition();

    return (
        <>
            <div className="min-h-screen bg-[var(--color-background)]">
                <Header />
                <motion.div {...pageTransition} className="bg-slate-50 dark:bg-slate-900">
                    <Helmet>
                        <title>Politique de Confidentialité - ORAZEK Investment</title>
                        <meta name="description" content="Politique de confidentialité d'ORAZEK Investment. Découvrez comment nous protégeons vos données personnelles." />
                        <meta name="robots" content="index, follow" />
                    </Helmet>

                    {/* Hero Section */}
                    <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-20">
                        <div className="max-w-4xl mx-auto px-6">
                            <div className="flex items-center mb-6">
                                <Icon name="Shield" size={48} className="text-blue-400" />
                            </div>
                            <h1 className="text-4xl md:text-5xl font-bold mb-4">Politique de Confidentialité</h1>
                            <p className="text-xl text-blue-200">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="max-w-4xl mx-auto px-6 py-16">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 md:p-12 space-y-8">

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Info" size={24} className="mr-3 text-blue-600" />
                                    1. Introduction
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    ORAZEK Investment s'engage à protéger la confidentialité et la sécurité de vos données personnelles. Cette politique de confidentialité explique quelles informations nous collectons, comment nous les utilisons, et vos droits concernant ces données.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Database" size={24} className="mr-3 text-blue-600" />
                                    2. Données Collectées
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Nous collectons les types de données suivants :
                                </p>
                                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span><strong>Données d'identification :</strong> nom, prénom, adresse email, numéro de téléphone</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span><strong>Données de navigation :</strong> adresse IP, type de navigateur, pages visitées</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span><strong>Données professionnelles :</strong> entreprise, fonction, secteur d'activité</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span><strong>Données transactionnelles :</strong> historique des demandes de services et interactions</span>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Target" size={24} className="mr-3 text-blue-600" />
                                    3. Utilisation des Données
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Nous utilisons vos données personnelles pour :
                                </p>
                                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Traiter vos demandes de services et devis</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Vous contacter concernant nos services</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Améliorer nos services et votre expérience utilisateur</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Vous envoyer des newsletters et communications marketing (avec votre consentement)</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Respecter nos obligations légales et réglementaires</span>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Lock" size={24} className="mr-3 text-blue-600" />
                                    4. Sécurité des Données
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, perte, destruction ou altération. Nos serveurs sont sécurisés et nous utilisons le chiffrement SSL pour toutes les transmissions de données sensibles.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Share2" size={24} className="mr-3 text-blue-600" />
                                    5. Partage des Données
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos informations avec :
                                </p>
                                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                                    <li className="flex items-start">
                                        <Icon name="Users" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Nos prestataires de services (hébergement, paiement, email) sous contrat de confidentialité</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="Users" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Les autorités légales si requis par la loi</span>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Clock" size={24} className="mr-3 text-blue-600" />
                                    6. Durée de Conservation
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Nous conservons vos données personnelles uniquement le temps nécessaire aux finalités pour lesquelles elles ont été collectées, ou conformément aux obligations légales. Les données des prospects sont conservées 3 ans maximum après le dernier contact.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="UserCheck" size={24} className="mr-3 text-blue-600" />
                                    7. Vos Droits
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Vous disposez des droits suivants concernant vos données personnelles :
                                </p>
                                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                                    <li className="flex items-start">
                                        <Icon name="Eye" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span><strong>Droit d'accès :</strong> obtenir une copie de vos données</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="Edit" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span><strong>Droit de rectification :</strong> corriger des données inexactes</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="Trash2" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span><strong>Droit à l'effacement :</strong> demander la suppression de vos données</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="StopCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="Download" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</span>
                                    </li>
                                </ul>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mt-4">
                                    Pour exercer ces droits, contactez-nous à : <a href="mailto:orazekinvestment5@gmail.com" className="text-blue-600 hover:underline font-medium">orazekinvestment5@gmail.com</a>
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Cookie" size={24} className="mr-3 text-blue-600" />
                                    8. Cookies
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Notre site utilise des cookies pour améliorer votre expérience. Vous pouvez configurer votre navigateur pour refuser les cookies, mais certaines fonctionnalités du site pourraient ne plus être disponibles.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Mail" size={24} className="mr-3 text-blue-600" />
                                    9. Contact
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Pour toute question concernant cette politique de confidentialité ou le traitement de vos données personnelles, contactez-nous :
                                </p>
                                <div className="mt-4 p-6 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                                    <p className="text-slate-900 dark:text-white font-medium">ORAZEK Investment</p>
                                    <p className="text-slate-700 dark:text-slate-300">Kinshasa, République Démocratique du Congo</p>
                                    <p className="text-slate-700 dark:text-slate-300">Email : <a href="mailto:orazekinvestment5@gmail.com" className="text-blue-600 hover:underline">orazekinvestment5@gmail.com</a></p>
                                    <p className="text-slate-700 dark:text-slate-300">Téléphone : <a href="tel:+243818883531" className="text-blue-600 hover:underline">+243 81 888 3531</a></p>
                                </div>
                            </section>

                        </div>
                    </div>
                </motion.div>
                <Footer />
            </div>
        </>
    );
};

export default PrivacyPolicy;

